import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PublisherPDF } from "../../api/compute/PublisherPDF";
import { useAuth } from "../../hooks/useAuth";

import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";

export default function PdfPublisher() {
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();
  const [pdfList, setPdfList] = useState([]);
  const [filteredPfdList, setFilteredPdfList] = useState(pdfList);

  useEffect(() => {
    setFilteredPdfList(pdfList);
  }, [pdfList]);

  const publisher = useMemo(() => {
    return new PublisherPDF();
  }, []);
  const getPdf = useCallback(async () => {
    setIsLoading(true);
    const data = await publisher.getPdfList(auth.user.additionalCookies);
    setPdfList(data
      // data.filter(function (item) {
      //   return item.type === "combined-product";
      // })
    );
    setIsLoading(false);
  }, [auth.user, publisher]);
  useEffect(() => {
    getPdf();
  }, [getPdf]);

  const uploadFile = useCallback(
    (file, whichFile: "portfolios_pdf" | "insights_json") => {
      const additionalFields = {
        additionalCookies: JSON.stringify(auth.user.additionalCookies),
        destination: whichFile,
      };

      for (const key in additionalFields) {
        file.append(key, additionalFields[key]);
      }

      return publisher.uploadPDF(file);
    },
    [auth.user.additionalCookies, publisher]
  );

  const searchHandler = useCallback(
    (e) => {
      let searchingFor: string = e.target.value.toString();
      if (searchingFor.length <= 0) {
        setFilteredPdfList(pdfList);
      } else {
        const result = pdfList.filter((item: any) => {
          let itemName: string = item.info.name.toString();
          itemName = itemName.toLowerCase();
          searchingFor = searchingFor.toLowerCase();
          return itemName.includes(searchingFor);
        });
        setFilteredPdfList(result);
      }
    },
    [pdfList]
  );

  const clearFilter = useCallback(() => {
    setFilteredPdfList(pdfList);
  }, [pdfList]);
  const sortedFilteredPfdList = useMemo(() => {
    if(filteredPfdList.length>0){

      return [...filteredPfdList].sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      );
    }else{
      return []
    }
}, [filteredPfdList]);

  return isLoading ? (
    <Box
      display={"flex"}
      gap={2}
      flexDirection={"column"}
      alignItems={"center"}
      width={"100%"}
      height={"100%"}
      justifyContent={"center"}
    >
      <Typography>Loading ...</Typography>
      <CircularProgress color="secondary" />
    </Box>
  ) : (
    <Box height={"100%"} width={"100%"} display={"flex"} minHeight={0} gap={2}>
      <Section title={"Portfolios PDFs"}>
        <Box display={"flex"} flexDirection={"column"} overflow={"auto"} p={2}>
          <Box px={2} display={"flex"} gap={2}>
            <CustomSearchBar
              clearFilter={clearFilter}
              onChangeHandler={searchHandler}
            />
          </Box>
          <Box
            display={"flex"}
            gap={2}
            flexDirection={"column"}
            overflow={"auto"}
            p={2}
          >
            {pdfList.length > 0 &&
              sortedFilteredPfdList.map((item: any, index) => (
                <Entry key={index} item={item} uploadFile={uploadFile} />
              ))}
          </Box>
        </Box>
      </Section>
      <Section title={"Insights Analytics Publisher"}>
        <InsightsEntry upload={uploadFile} />
      </Section>
    </Box>
  );
}

const Section = ({ children, title }) => {
  const theme = useTheme();

  return (
    <Card sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Box p={2} bgcolor={theme.palette.secondary.main}>
        <Typography
          sx={{ fontSize: "1vw", color: theme.palette.primary.light }}
        >
          {title}
        </Typography>
      </Box>
      <CardContent sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {children}
      </CardContent>
    </Card>
  );
};

const getName = (item) => {
  return item.info.name;
};
type EntryType = {
  item: any;
  uploadFile: (
    data: FormData,
    whichFile: "portfolios_pdf" | "insights_json"
  ) => Promise<void>;
};
const Entry = ({ item, uploadFile }: EntryType) => {
  const fileRef = useRef<any>(null);
  const [selectedFile, setSelectedFile] = useState<FormData | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const feedback = useFeedbackSystem();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const auth = useAuth();

  const clearInput = useCallback(() => {
    setSelectedFile(null);
    setSelectedFileName(null);
    const input = fileRef.current;

    if (input) {
      input.value = "";
    }
  }, []);

  const publisher = useMemo(() => {
    return new PublisherPDF();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const fileObj = event.target.files[0];
        if (!fileObj) {
          return;
        }
        if (fileObj.type !== "application/pdf") {
          feedback.show({
            type: "error",
            message: "only PDF format is allowed!",
          });
        } else {
          let data = new FormData();
          data.append("name", item.name);
          data.append("type", item.type);
          data.append("file", fileObj);
          setSelectedFile(data);
          setSelectedFileName(fileObj.name);
        }
      }
    },
    [feedback, item.name, item.type]
  );
  const downloadFile = useCallback(async () => {
    setIsDownloading(true);
    try {
      const response = await publisher.downloadPDF({
        type: item.type,
        name: item.name,
        additionalCookies: auth.user.additionalCookies,
      });
      if (response.ok) {
        const blob = await response.blob();
        const newBlob = new Blob([blob], { type: "application/pdf" });
        const objUrl = window.URL.createObjectURL(newBlob);
        let link = document.createElement("a");
        link.href = objUrl;
        link.download = item.name;
        link.click();
        link.remove();
      } else {
        throw Error;
      }
    } catch (error) {
      feedback.show({
        type: "error",
        message: "file does not exist in S3!",
      });
    }
    setIsDownloading(false);
  }, [auth.user.additionalCookies, feedback, item.name, item.type, publisher]);

  return (
    <Box
      key={item.name}
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
      p={1}
      sx={{ boxShadow: 2, borderRadius: 2 }}
    >
      <span onClick={downloadFile} style={{ cursor: "pointer" }}>
        <Typography display={"flex"} alignItems={"center"} gap={2}>
          <strong>{getName(item)}</strong>
          {isDownloading && <CircularProgress color="secondary" size={15} />}
        </Typography>
      </span>
      <Box display={"flex"} gap={2}>
        {selectedFile != null && (
          <Chip
            color="secondary"
            label={selectedFileName}
            onDelete={clearInput}
          />
        )}
        {selectedFile == null ? (
          <Button
            onClick={() => {
              fileRef?.current?.click();
            }}
          >
            Select file
          </Button>
        ) : (
          <Button
            color="secondary"
            onClick={() => {
              setIsUploading(true);
              uploadFile(selectedFile, "portfolios_pdf").then(
                (e) => {
                  clearInput();
                  setIsUploading(false);
                  feedback.show({
                    type: "success",
                    message: "File uploaded successfully!",
                  });
                },
                (error) => {
                  feedback.show({
                    type: "error",
                    message: "something went wrong..." + error,
                  });
                  setIsUploading(false);
                }
              );
            }}
          >
            {!isUploading ? "Upload file" : "uploading"}
            {isUploading && (
              <>
                <Fragment>&nbsp;&nbsp;</Fragment> <CircularProgress size={20} />
              </>
            )}
          </Button>
        )}
        <input
          onChange={handleFileChange}
          style={{ display: "none" }}
          ref={fileRef}
          type="file"
        />
      </Box>
    </Box>
  );
};

type InsightsEntryProps = {
  upload: (
    data: FormData,
    whichFile: "portfolios_pdf" | "insights_json"
  ) => Promise<void>;
};

const InsightsEntry = ({ upload }: InsightsEntryProps) => {
  const [selectedFile, setSelectedFile] = useState<FormData>();
  const [isUploading, setIsUploading] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const feedback = useFeedbackSystem();

  const selectedFileName = useMemo(
    () => selectedFile?.get("name") ?? "",
    [selectedFile]
  );

  const clearInput = useCallback(() => {
    setSelectedFile(undefined);
    const input = fileRef.current;

    if (input) {
      input.value = "";
    }
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const fileObj = event.target.files[0];
        if (!fileObj) {
          return;
        }
        if (fileObj.type !== "application/json") {
          feedback.show({
            type: "error",
            message: "only .json extension is allowed!",
          });
        } else {
          let data = new FormData();
          data.append("name", fileObj.name);
          data.append("type", fileObj.type);
          data.append("file", fileObj);
          setSelectedFile(data);
        }
      }
    },
    [feedback]
  );

  const onUploadFinish = useCallback(() => {
    clearInput();
    setIsUploading(false);
    feedback.show({
      type: "success",
      message: "File uploaded successfully!",
    });
  }, [clearInput, feedback]);

  const uploadFile = useCallback(async () => {
    setIsUploading(true);

    if (selectedFile) {
      try {
        await upload(selectedFile, "insights_json");
        onUploadFinish();
      } catch (error) {
        feedback.show({
          type: "error",
          message: "something went wrong..." + error,
        });
        setIsUploading(false);
      }
    } else {
      feedback.show({
        type: "error",
        message: "No file is selected for upload",
      });
    }
  }, [feedback, onUploadFinish, selectedFile, upload]);

  return (
    <Box p={2} width={"60%"}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        p={1}
        sx={{ boxShadow: 2, borderRadius: 2 }}
      >
        <span>
          <Typography display={"flex"} alignItems={"center"} gap={2}>
            <strong>Insights Analytics JSON</strong>
          </Typography>
        </span>
        <Box display={"flex"} gap={2}>
          {selectedFile != null && (
            <Chip
              sx={{ maxWidth: "10em" }}
              color="secondary"
              label={(selectedFileName as string) ?? ""}
              onDelete={clearInput}
            />
          )}
          {selectedFile == null ? (
            <Button
              onClick={() => {
                fileRef?.current?.click();
              }}
            >
              Select file
            </Button>
          ) : (
            <Button color="secondary" onClick={uploadFile}>
              {!isUploading ? "Upload file" : "uploading"}
              {isUploading && (
                <>
                  <Fragment>&nbsp;&nbsp;</Fragment>{" "}
                  <CircularProgress size={20} />
                </>
              )}
            </Button>
          )}
          <input
            onChange={handleFileChange}
            style={{ display: "none" }}
            ref={fileRef}
            type="file"
          />
        </Box>
      </Box>
    </Box>
  );
};

type CustomSearchBarType = {
  onChangeHandler: Function;
  clearFilter: Function;
};

function CustomSearchBar({
  onChangeHandler,
  clearFilter,
}: CustomSearchBarType) {
  const [str, setStr] = useState("");
  return (
    <Paper
      component="form"
      sx={{ px: 1, display: "flex", alignItems: "center", width: 400 }}
    >
      <InputBase
        onKeyDown={(e) => {
          //!preventing  page reloading
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
          }
          return;
        }}
        value={str}
        sx={{ ml: 1, flex: 1 }}
        placeholder="Search"
        onChange={(e) => {
          onChangeHandler(e);
          setStr(e.target.value);
        }}
      />
      <IconButton disabled type="button" sx={{ p: "10px" }} aria-label="search">
        <SearchIcon />
      </IconButton>
      <Button
        color="secondary"
        disabled={str === ""}
        onClick={() => {
          setStr("");
          clearFilter();
        }}
      >
        clear filter
      </Button>
    </Paper>
  );
}
